import React, { useMemo, useState } from 'react';
import Icons from '../Icons';

const normalize = (value) => (value || '').toString().toLowerCase();

export default function SearchableChecklist({
  id,
  items = [],
  selectedIds = [],
  onToggle,
  getId = (item) => item.id ?? item.value ?? item,
  getLabel = (item) => item.label ?? item.name ?? String(item),
  getMeta,
  placeholder = 'Search...',
  emptyLabel = 'No results found.',
  maxVisible = 10,
  disabled = false,
  className = '',
  listClassName = '',
  ariaLabel
}) {
  const [query, setQuery] = useState('');
  const listId = `${id || 'search-checklist'}-list`;

  const filteredItems = useMemo(() => {
    if (!query) return items;
    const needle = normalize(query);
    return items.filter((item) => {
      const label = normalize(getLabel(item));
      const meta = normalize(getMeta ? getMeta(item) : '');
      return label.includes(needle) || meta.includes(needle);
    });
  }, [items, query, getLabel, getMeta]);

  const maxHeight = maxVisible ? `${maxVisible * 36}px` : undefined;

  return (
    <div className={`sp-search-list ${className}`.trim()}>
      <div className={`sp-search sp-search-select__input ${disabled ? 'is-disabled' : ''}`.trim()}>
        <Icons.Search />
        <input
          id={id}
          aria-controls={listId}
          value={query}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel || placeholder}
        />
        {query && !disabled && (
          <button
            type="button"
            className="sp-clear sp-clear--muted"
            aria-label="Clear search"
            onClick={() => setQuery('')}
          >
            <Icons.X />
          </button>
        )}
      </div>
      <div
        id={listId}
        className={`sp-search-list__items ${listClassName}`.trim()}
        role="list"
        aria-label={ariaLabel || 'Search results'}
        style={{ maxHeight }}
      >
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => {
            const itemId = getId(item);
            const label = getLabel(item);
            const meta = getMeta ? getMeta(item) : '';
            const isSelected = selectedIds.includes(itemId);
            return (
              <label key={itemId} className={`sp-search-option sp-search-option--checkbox ${isSelected ? 'is-selected' : ''}`.trim()}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onToggle(itemId)}
                />
                <span>{label}</span>
                {meta && <span className="sp-search-option__meta">{meta}</span>}
              </label>
            );
          })
        ) : (
          <div className="sp-search-empty">{emptyLabel}</div>
        )}
      </div>
      <div className="text-xs sp-muted" aria-live="polite">
        {filteredItems.length} result{filteredItems.length === 1 ? '' : 's'}
      </div>
    </div>
  );
}
